import { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Stack,
    Typography,
    Chip,
    Tooltip,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import DialogBox from "components/lib-ui/DialogBox";
import SubscriptionForm from "./SubscriptionForm";
import GeneralUtilityService from "services/GeneralUtilityService";
import StripeIcon from "assets/icons/StripeIcon";
import MainTheme from "assets/theme/MainTheme";
import SubscriptionRowActions from "./SubscriptionActions";
import NotDataIcon from "assets/icons/NoDataIcon";
import {
    getSubscriptionStatusBackgroundColor,
    getSubscriptionStatusFromRow,
    getSubscriptionStatusLabel,
    getSubscriptionDurationLabel,
    isActiveOrTrialSubscription,
    SUBSCRIPTION_STATUSES,
} from "utils/subscriptionStatus";

const TAGS_COLUMN_WIDTH = 200;

export default function SubscriptionsTable({
    rows,
    talentRequests = 0,
    jobSlots = 0,
}) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isNotificationDialogOpen, setIsNotificationDialogOpen] =
        useState(false);

    const subscriptionRows = rows || [];

    const hasActiveSubscription = subscriptionRows?.some(
        isActiveOrTrialSubscription,
    );

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    return (
        <>
            <DialogBox
                isDialogOpen={isNotificationDialogOpen}
                setIsDialogOpen={setIsNotificationDialogOpen}
                floatCancel={true}
                title="Reminder ⚠️"
                showActionButtons={false}
            >
                <Typography py={2} variant="body1">
                    After changing a company's subscription plan, make sure to
                    update their job slot credits and linq requests accordingly.
                </Typography>
            </DialogBox>

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 4,
                }}
            >
                <Stack
                    p={2}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: "100%" }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Subscription Management
                    </Typography>
                    <Tooltip
                        title="Cannot add a new subscription while there is an active or trial subscription."
                        disableHoverListener={!hasActiveSubscription}
                    >
                        <span>
                            <Button
                                sx={{
                                    borderRadius: "50px",
                                    textTransform: "none",
                                }}
                                disabled={hasActiveSubscription}
                                size="small"
                                color="primary"
                                startIcon={<Add />}
                                variant="contained"
                                onClick={handleOpenDialog}
                                aria-label="Add new subscription"
                            >
                                Add Subscription
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "3rem" }}>#</TableCell>
                            <TableCell>Package</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell>Renewal Times</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Canceled</TableCell>
                            <TableCell sx={{ width: TAGS_COLUMN_WIDTH }}>
                                Tags
                            </TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {subscriptionRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    <Stack
                                        alignItems="center"
                                        pb={3}
                                        sx={{ width: "100%" }}
                                    >
                                        <Stack
                                            alignItems="center"
                                            width="150px"
                                        >
                                            <NotDataIcon width="150px" />
                                        </Stack>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                        >
                                            No subscription data available.
                                        </Typography>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ) : (
                            subscriptionRows.map((row, index) => {
                                const status =
                                    getSubscriptionStatusFromRow(row);
                                const isActive =
                                    status === SUBSCRIPTION_STATUSES.ACTIVE;
                                const isStripeSubscription =
                                    !!row?.stripeSubscription?.id;
                                const tags = isStripeSubscription
                                    ? []
                                    : row?.tags || [];

                                const statusLabel =
                                    getSubscriptionStatusLabel(status);
                                const remainingDaysLabel =
                                    getSubscriptionDurationLabel(row, status);

                                return (
                                    <TableRow
                                        key={`${row.id}/${index}`}
                                        sx={{
                                            "&:last-child td, &:last-child th":
                                                {
                                                    border: 0,
                                                },
                                        }}
                                    >
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            {row.package.name || ""}
                                        </TableCell>
                                        <TableCell>
                                            {remainingDaysLabel}
                                        </TableCell>
                                        <TableCell>
                                            {GeneralUtilityService.formattedDate(
                                                row?.startAt,
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {GeneralUtilityService.formattedDate(
                                                row?.endAt,
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row?.timesRenewed}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                variant="filled"
                                                label={statusLabel}
                                                sx={{
                                                    backgroundColor:
                                                        getSubscriptionStatusBackgroundColor(
                                                            status,
                                                        ),
                                                    color: "#000",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {row?.cancellationDate
                                                ? GeneralUtilityService.formattedDate(
                                                      row.cancellationDate,
                                                  )
                                                : ""}
                                        </TableCell>
                                        <TableCell>
                                            <Stack
                                                direction="row"
                                                flexWrap="wrap"
                                                gap={0.5}
                                            >
                                                {tags.map((tag) => (
                                                    <Chip
                                                        key={tag.id}
                                                        size="small"
                                                        label={tag.name}
                                                        sx={{
                                                            fontSize: "0.7rem",
                                                            height: 20,
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {isStripeSubscription ? (
                                                <StripeIcon
                                                    fill={
                                                        MainTheme.palette
                                                            .grey[500]
                                                    }
                                                    fontSize="large"
                                                />
                                            ) : (
                                                <Chip
                                                    sx={{
                                                        background: "#f3e5f5",
                                                    }}
                                                    label="MANUAL"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {!isStripeSubscription && (
                                                <SubscriptionRowActions
                                                    subscriptionId={row.id}
                                                    isActive={isActive}
                                                    plan={row.package}
                                                    startAt={row.startAt}
                                                    endAt={row.endAt}
                                                    tags={tags}
                                                    setIsNotificationDialogOpen={
                                                        setIsNotificationDialogOpen
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <DialogBox
                isDialogOpen={isDialogOpen}
                setIsDialogOpen={setIsDialogOpen}
                title="Create Subscription"
                floatCancel
                showActionButtons={false}
            >
                <SubscriptionForm
                    setIsDialogOpen={setIsDialogOpen}
                    talentRequests={talentRequests}
                    jobSlots={jobSlots}
                />
            </DialogBox>
        </>
    );
}
